import { useEffect, useMemo, useState } from "react";
import { apiRequest } from "../../../shared/api/client";
import type { ProductionReferenceData } from "../../production-execution/models";
import type { Mapping, RouteCandidate, RouteEvidence } from "./settings-types";
import type { Dispatch, SetStateAction } from "react";
type Setter<T> = Dispatch<SetStateAction<T>>;
import { evidenceNote } from "./route-evidence";
export function usePreparationRouteWorkspace({ reference, lineId, productCode, setError, notify }: {
    reference: ProductionReferenceData | null;
    lineId: string;
    productCode: string;
    setError: Setter<string | null>;
    notify: (message: string) => void;
}) {
    const [stagingWarehouseCode, setStagingWarehouseCode] = useState("");
    const [mapping, setMapping] = useState<Mapping | null>(null);
    const [candidate, setCandidate] = useState<RouteCandidate | null>(null);
    const [evidence, setEvidence] = useState<RouteEvidence | null>(null);
    const [note, setNote] = useState("");
    const [working, setWorking] = useState(false);
    useEffect(() => {
        if (!lineId)
            return;
        let current = true;
        setCandidate(null);
        setEvidence(null);
        apiRequest<Mapping>(`/api/material-preparation/warehouse-mappings/${lineId}`, {
            cache: "no-store",
        })
            .then((saved) => {
            if (!current)
                return;
            setMapping(saved);
            setStagingWarehouseCode(saved.stagingWarehouseCode);
            setNote(saved.note);
        })
            .catch(() => {
            if (!current)
                return;
            setMapping(null);
            setStagingWarehouseCode("");
            setNote("");
        });
        return () => {
            current = false;
        };
    }, [lineId]);
    const productOptions = useMemo(() => {
        const selectedLine = reference?.lines.find((line) => line.id === lineId);
        const seen = new Set<string>();
        const options = [
            ...(reference?.scheduleItems ?? [])
                .filter((item) => item.lineId === lineId && item.productCode)
                .map((item) => ({ code: item.productCode!, name: item.label })),
            ...(reference?.workOrders ?? [])
                .filter((order) => !selectedLine ||
                order.plannedWorkCenterCode === selectedLine.mikroWorkCenterCode ||
                order.plannedWorkCenterName === selectedLine.name)
                .map((order) => ({ code: order.productCode, name: order.productName })),
        ];
        return options.filter((option) => {
            if (seen.has(option.code))
                return false;
            seen.add(option.code);
            return true;
        });
    }, [lineId, reference]);
    const suggest = async () => {
        if (!productCode.trim() || !stagingWarehouseCode) {
            setError("Rota kanıtı için ürün ve hedef cep depo seçilmelidir.");
            return;
        }
        setWorking(true);
        setError(null);
        try {
            const result = await apiRequest<RouteEvidence>(`/api/material-preparation/route-suggestions?productCode=${encodeURIComponent(productCode.trim())}&stagingWarehouseCode=${encodeURIComponent(stagingWarehouseCode)}&lookbackDays=365`, { cache: "no-store" });
            setEvidence(result);
            setCandidate(null);
            if (!result.candidates.length)
                setError("Bu ürünün reçete malzemeleri için hedef cep depoya uzanan doğrulanabilir bir transfer rotası bulunamadı.");
        }
        catch (suggestionError) {
            setEvidence(null);
            setCandidate(null);
            setError(suggestionError instanceof Error
                ? suggestionError.message
                : "Mikro hareket geçmişi okunamadı.");
        }
        finally {
            setWorking(false);
        }
    };
    const selectCandidate = (selected: RouteCandidate) => {
        setCandidate(selected);
        setStagingWarehouseCode(selected.stagingWarehouse.code);
        setNote(evidenceNote(selected, productCode.trim(), evidence?.lookbackDays ?? 365));
    };
    const save = async () => {
        if (!lineId || !candidate || note.trim().length < 10) {
            setError("Kaydetmek için rota önerisini seçin ve doğrulama gerekçesini yazın.");
            return;
        }
        setWorking(true);
        setError(null);
        try {
            const saved = await apiRequest<Mapping>(`/api/material-preparation/warehouse-mappings/${lineId}`, {
                method: "PUT",
                body: JSON.stringify({
                    mainWarehouseCode: candidate.mainWarehouse.code,
                    handoffWarehouseCode: candidate.handoffWarehouse.code,
                    stagingWarehouseCode: candidate.stagingWarehouse.code,
                    note: note.trim(),
                    expectedVersion: mapping?.version,
                }),
            });
            setMapping(saved);
            notify(`${candidate.mainWarehouse.code} → ${candidate.handoffWarehouse.code} → ${candidate.stagingWarehouse.code} hazırlık rotası kaydedildi.`);
        }
        catch (saveError) {
            setError(saveError instanceof Error
                ? saveError.message
                : "Hazırlık rotası kaydedilemedi.");
        }
        finally {
            setWorking(false);
        }
    };
    return { stagingWarehouseCode, setStagingWarehouseCode, mapping, candidate, evidence, note, setNote, working, productOptions, suggest, selectCandidate, save };
}
